"use client";

import { useState, useEffect } from "react";
import {
  doc,
  collection,
  onSnapshot,
  query,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

// Real-time listener for a single document
export function useFirestoreDoc<T extends { uid?: string }>(
  path: string | null,
  documentId: string | null
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!path || !documentId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = doc(db, path, documentId);
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (snapshot.exists()) {
          setData({ ...snapshot.data(), uid: snapshot.id } as T);
        } else {
          setData(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [path, documentId]);

  return { data, loading, error };
}

// Real-time listener for a collection.
//
// `constraints` are passed straight to `query(...)`. When constraint
// *values* (not just types) change at runtime — e.g. switching the
// `arrayContains` argument on a filter pill — the caller must supply
// an explicit `key` so the effect re-subscribes. The fallback hashing
// only inspects constraint *type* names (`"where"`, `"orderBy"`,
// `"limit"`), which is enough for callers that always pass the same
// static constraints, but invisible to value swaps.
export function useFirestoreCollection<T extends { uid?: string }>(
  path: string | null,
  constraints: QueryConstraint[] = [],
  key?: string,
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const constraintsKey =
    key ?? JSON.stringify(constraints.map((c) => c.type));

  useEffect(() => {
    if (!path) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = collection(db, path);
    const q = constraints.length > 0 ? query(ref, ...constraints) : query(ref);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(
          (d) => ({ ...d.data(), uid: d.id }) as T
        );
        setData(docs);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, constraintsKey]);

  return { data, loading, error };
}
